import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { TenantStorage } from '../utils/tenantStorage';
import { getTenantIdFromUrl } from '../api/apiUtils';

interface TenantContextValue {
  tenantId: string | null;
  storage: TenantStorage | null;
  isReady: boolean;
  error: Error | null;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * TenantContext Provider
 * 在 App 級別初始化租戶識別與 TenantStorage
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [storage, setStorage] = useState<TenantStorage | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastCheckedSearchRef = useRef<string>('');
  const tenantIdRef = useRef<string | null>(null);
  const storageRef = useRef<TenantStorage | null>(null);
  const isReadyRef = useRef<boolean>(false);

  // 同步 ref 和 state，並追蹤狀態變化
  const previousTenantIdRef = useRef<string | null>(null);
  const previousIsReadyRef = useRef<boolean>(false);
  useEffect(() => {
    const timestamp = new Date().toISOString();
    const tenantIdChanged = previousTenantIdRef.current !== tenantId;
    if (tenantIdChanged) {
      console.log(`[TenantContext] [${timestamp}] 🔄 tenantId 狀態變化:`, {
        from: previousTenantIdRef.current,
        to: tenantId,
      });
      previousTenantIdRef.current = tenantId;
    }
    tenantIdRef.current = tenantId;
  }, [tenantId]);

  useEffect(() => {
    const timestamp = new Date().toISOString();
    const isReadyChanged = previousIsReadyRef.current !== isReady;
    if (isReadyChanged) {
      console.log(`[TenantContext] [${timestamp}] 🔄 isReady 狀態變化:`, {
        from: previousIsReadyRef.current,
        to: isReady,
        currentTenantId: tenantIdRef.current,
      });
      previousIsReadyRef.current = isReady;
    }
    isReadyRef.current = isReady;
  }, [isReady]);

  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  // 當 location 改變時立即檢查
  useEffect(() => {
    // Lite 路由（/lite, /lite/*）不依賴 tenantId，使用 groupId；直接設為就緒，跳過 tenant 輪詢與 groupId→tenantId 解析
    if (location.pathname.startsWith('/lite')) {
      setTenantId(null);
      setStorage(null);
      setError(null);
      setIsReady(true);
      return;
    }

    // 檢查並初始化 tenantId 的函數
    const checkAndInitializeTenant = () => {
      // 使用 getTenantIdFromUrl() 函數，與 API 調用邏輯完全一致
      // 這個函數會檢查多種參數名稱：tenantId, tenant_id, rent_id, rentId
      const id = getTenantIdFromUrl();
      const currentTenantId = tenantIdRef.current;
      const currentSearch = window.location.search;

      // 如果 search 沒有變化且 tenantId 也沒變化，跳過
      if (
        currentSearch === lastCheckedSearchRef.current &&
        id === currentTenantId
      ) {
        return;
      }

      lastCheckedSearchRef.current = currentSearch;
      const timestamp = new Date().toISOString();
      console.log(
        `[TenantContext] [${timestamp}] ========== checkAndInitializeTenant 開始 ==========`
      );
      console.log(`[TenantContext] [${timestamp}] 檢查 URL 參數:`, {
        locationSearch: location.search,
        windowLocationSearch: currentSearch,
        urlFromId: id,
        currentTenantIdState: currentTenantId,
        lastCheckedSearch: lastCheckedSearchRef.current,
      });

      if (!id || id.trim() === '') {
        // 付款完成頁（/preorder/payment/return）常只帶 orderNumber，不帶 tenantId；保留既有 tenantId 供確認付款使用
        if (
          currentTenantId &&
          (location.pathname.includes('/preorder/payment/return') ||
            window.location.pathname.includes('/preorder/payment/return'))
        ) {
          return;
        }
        // 如果之前已經有 tenantId，但現在沒有了，可能是 URL 被清空了
        if (currentTenantId) {
          console.warn(
            `[TenantContext] [${timestamp}] ⚠️ tenantId 從 URL 中消失，清空狀態`
          );
          setTenantId(null);
          setStorage(null);
          setError(null);
          setIsReady(false);
          return;
        }
        // 沒有 tenantId：保持等待，由輪詢或 fallback 處理（Lite 已在前方 path 判斷跳過，不會進到這裡）
        setError(null);
        setIsReady(false);
        return;
      }

      const trimmedId = id.trim();

      // 如果 tenantId 沒有變化，不需要重新初始化
      if (currentTenantId === trimmedId && storageRef.current) {
        console.log(
          `[TenantContext] [${timestamp}] ⏭️ tenantId 未變化 (${trimmedId})，跳過重新初始化`
        );
        return;
      }

      console.log(`[TenantContext] [${timestamp}] 🚀 開始初始化租戶:`, {
        tenantId: trimmedId,
        previousTenantId: currentTenantId,
        previousIsReady: isReadyRef.current,
      });

      try {
        // 建立 TenantStorage 實例
        const tenantStorage = new TenantStorage(trimmedId);

        console.log(`[TenantContext] [${timestamp}] 📝 準備更新狀態:`, {
          tenantId: trimmedId,
          isReady: true,
        });
        setTenantId(trimmedId);
        setStorage(tenantStorage);
        setError(null);
        setIsReady(true);

        console.log(`[TenantContext] [${timestamp}] ✅ 租戶初始化成功:`, {
          tenantId: trimmedId,
          isReady: true,
        });
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '租戶初始化失敗';
        console.error(`[TenantContext] [${timestamp}] ❌ 租戶初始化失敗:`, {
          error: errorMsg,
          stack: err instanceof Error ? err.stack : undefined,
          tenantId: trimmedId,
        });
        setError(new Error(errorMsg));
        setIsReady(true);
        console.log(
          `[TenantContext] [${timestamp}] ⚠️ 設置 isReady = true (異常情況)`
        );
      }
      console.log(
        `[TenantContext] [${timestamp}] ========== checkAndInitializeTenant 結束 ==========`
      );
    };

    checkAndInitializeTenant();
  }, [location.search, location.pathname]);

  // 添加輪詢機制，定期檢查 URL 參數（用於 LIFF 環境下動態添加 URL 參數的情況）
  useEffect(() => {
    // Lite 路由不使用 tenantId，不輪詢
    if (location.pathname.startsWith('/lite')) {
      return;
    }
    // 如果已經就緒且有 tenantId，不需要輪詢
    if (isReady && tenantId) {
      return;
    }

    // 檢查並初始化 tenantId 的函數（用於輪詢）
    const checkAndInitializeTenantForPolling = () => {
      // 使用 getTenantIdFromUrl() 函數，與 API 調用邏輯完全一致
      const id = getTenantIdFromUrl();
      const currentTenantId = tenantIdRef.current;
      const currentSearch = window.location.search;

      // 如果 search 沒有變化且 tenantId 也沒變化，跳過
      if (
        currentSearch === lastCheckedSearchRef.current &&
        id === currentTenantId
      ) {
        return;
      }

      lastCheckedSearchRef.current = currentSearch;
      const timestamp = new Date().toISOString();
      console.log(
        `[TenantContext] [${timestamp}] [輪詢] ========== checkAndInitializeTenantForPolling 開始 ==========`
      );
      console.log(`[TenantContext] [${timestamp}] [輪詢] 檢查 URL 參數:`, {
        windowLocationSearch: currentSearch,
        urlFromId: id,
        currentTenantIdState: currentTenantId,
        lastCheckedSearch: lastCheckedSearchRef.current,
      });

      if (!id || id.trim() === '') {
        return;
      }

      const trimmedId = id.trim();

      // 如果 tenantId 沒有變化，不需要重新初始化
      if (currentTenantId === trimmedId && storageRef.current) {
        console.log(
          `[TenantContext] [${timestamp}] [輪詢] ⏭️ tenantId 未變化 (${trimmedId})，跳過重新初始化`
        );
        return;
      }

      console.log(`[TenantContext] [${timestamp}] [輪詢] 🚀 開始初始化租戶:`, {
        tenantId: trimmedId,
        previousTenantId: currentTenantId,
        previousIsReady: isReadyRef.current,
      });

      try {
        const tenantStorage = new TenantStorage(trimmedId);

        console.log(`[TenantContext] [${timestamp}] [輪詢] 📝 準備更新狀態:`, {
          tenantId: trimmedId,
          isReady: true,
        });
        setTenantId(trimmedId);
        setStorage(tenantStorage);
        setError(null);
        setIsReady(true);

        console.log(
          `[TenantContext] [${timestamp}] [輪詢] ✅ 租戶初始化成功:`,
          {
            tenantId: trimmedId,
            isReady: true,
          }
        );
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '租戶初始化失敗';
        console.error(
          `[TenantContext] [${timestamp}] [輪詢] ❌ 租戶初始化失敗:`,
          {
            error: errorMsg,
            stack: err instanceof Error ? err.stack : undefined,
            tenantId: trimmedId,
          }
        );
        setError(new Error(errorMsg));
        setIsReady(true);
        console.log(
          `[TenantContext] [${timestamp}] [輪詢] ⚠️ 設置 isReady = true (異常情況)`
        );
      }
      console.log(
        `[TenantContext] [${timestamp}] [輪詢] ========== checkAndInitializeTenantForPolling 結束 ==========`
      );
    };

    // 先立即執行一次檢查，再啟動輪詢（LIFF 環境下 URL 參數可能已就緒，可縮短等待）
    checkAndInitializeTenantForPolling();

    // 每 100ms 檢查一次 URL 參數，直到找到 tenantId 或超過 3 秒（優化：減少輪詢間隔和最大時間）
    const startTime = Date.now();
    const maxDuration = 3000; // 3 秒（從 10 秒減少到 3 秒）
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed > maxDuration) {
        console.log('[TenantContext] 輪詢超時，停止檢查');
        clearInterval(interval);
        // 如果超時還沒有找到 tenantId，允許 isReady = true 但 tenantId = null
        // 這樣組件可以使用 getTenantIdFromUrl() 作為 fallback
        if (!tenantIdRef.current) {
          console.warn(
            '[TenantContext] 輪詢超時，未找到 tenantId，允許組件使用 getTenantIdFromUrl() 作為 fallback'
          );
          setError(null); // 不設置錯誤，允許組件繼續運行
          setIsReady(true); // 設置為就緒，讓組件可以嘗試其他方式獲取 tenantId
        }
        return;
      }

      checkAndInitializeTenantForPolling();

      // 如果已經就緒且有 tenantId，停止輪詢（使用 ref 獲取最新值）
      if (tenantIdRef.current) {
        console.log('[TenantContext] 找到 tenantId，停止輪詢');
        clearInterval(interval);
      }
    }, 100); // 從 500ms 減少到 100ms

    return () => {
      clearInterval(interval);
    };
  }, [isReady, tenantId, location.pathname]); // 只在未就緒時輪詢；Lite  path 不輪詢

  const value: TenantContextValue = {
    tenantId,
    storage,
    isReady,
    error,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

/**
 * Hook to use Tenant context
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
